import { ArrowRight, Check } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import NinetyDayTimeline from "@/components/NinetyDayTimeline";
import { SERVICES, FRACTIONAL, CALENDLY_URL } from "@/content";

export default function Services() {
  return (
    <div data-testid="services-page">
      <SEO
        title="Services | Market Opportunity Intelligence, Target Accounts & Decision-Maker Research | Thrive Business Development"
        description="Explore Thrive's business development services: market mapping, target account identification, decision-maker research, commercial market intelligence, CRM enrichment, advisory and fractional business development."
      />

      <section className="relative overflow-hidden pt-40 pb-20 sm:pt-48 sm:pb-24" data-testid="services-hero">
        <div className="absolute inset-0 dot-grid-bg opacity-50" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="eyebrow mb-6">Services</p>
            <h1 className="font-display max-w-3xl text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-[-0.035em] leading-[1.02] text-white">
              Business Development Built Around Your Market
            </h1>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-8 max-w-2xl text-lg text-slate-300 leading-relaxed">
              Effective business development starts with knowing where to focus.
            </p>
            <p className="mt-4 max-w-2xl text-base text-slate-400 leading-relaxed">
              Thrive helps businesses understand their addressable market, identify priority accounts
              and build clearer routes into the organisations they want to work with.
            </p>
          </Reveal>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {SERVICES.map((s, i) => (
          <section
            key={s.id}
            id={s.id}
            data-testid={`service-section-${s.id}`}
            className="scroll-mt-28 border-t border-white/10 py-16 lg:py-24"
          >
            <div className="grid gap-10 lg:grid-cols-[220px_1fr] lg:gap-20">
              <Reveal>
                <span className="font-mono text-4xl text-white/15">{s.num}</span>
                <h2 className="mt-4 font-display text-2xl sm:text-3xl font-bold tracking-[-0.03em] text-white">
                  {s.title}
                </h2>
                <p className="mt-4 text-sm text-electric/80 leading-relaxed">{s.short}</p>
              </Reveal>

              <div className="grid gap-10 md:grid-cols-3 md:gap-8">
                <Reveal delay={0.08}>
                  <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-slate-500">
                    The Challenge
                  </h3>
                  <p className="prose-justify mt-4 text-sm text-slate-400 leading-relaxed">{s.challenge}</p>
                </Reveal>
                <Reveal delay={0.16}>
                  <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-slate-500">
                    What Thrive Does
                  </h3>
                  <p className="prose-justify mt-4 text-sm text-slate-400 leading-relaxed">{s.does}</p>
                </Reveal>
                <Reveal delay={0.24}>
                  <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-gold/80">
                    What You Receive
                  </h3>
                  <ul className="mt-4 space-y-3">
                    {s.receive.map((r) => (
                      <li key={r} className="flex gap-3 text-sm text-slate-300 leading-relaxed">
                        <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-gold" aria-hidden="true" />
                        {r}
                      </li>
                    ))}
                  </ul>
                </Reveal>
              </div>
            </div>
          </section>
        ))}
      </div>

      <section
        id={FRACTIONAL.id}
        data-testid="fractional-section"
        className="scroll-mt-28 relative overflow-hidden border-t border-white/10 py-20 lg:py-32"
      >
        <div className="absolute inset-0 dot-grid-bg opacity-40" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative grid gap-12 lg:grid-cols-[1fr_1fr] lg:gap-20">
            <div className="absolute inset-y-0 left-1/2 hidden w-px bg-white/5 lg:block" aria-hidden="true" />
            <Reveal>
              <div className="flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.25em]">
                <span className="text-gold">{FRACTIONAL.num}</span>
                <span className="h-px w-8 bg-gold/50" aria-hidden="true" />
                <span className="text-slate-500">Flexible Engagement</span>
              </div>
              <h2 className="mt-4 font-display text-2xl sm:text-3xl font-bold tracking-[-0.03em] text-white">
                {FRACTIONAL.title}
              </h2>
              <p className="mt-4 text-sm text-electric/80 leading-relaxed">
                Senior BD support on a flexible basis.
              </p>
              <p className="prose-justify mt-8 max-w-md text-base text-slate-400 leading-relaxed">
                For businesses that need leadership-level commercial focus now, not after a six-month
                recruitment process. Thrive brings senior business development capability into your
                team for the time it takes to build focus, pipeline and momentum.
              </p>
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="fractional-discuss-goals-button"
                className="group mt-10 inline-flex items-center gap-2 bg-gold px-7 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-navy transition-all duration-300 hover:bg-gold-glow"
              >
                Discuss Your Goals
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </Reveal>

            <div className="flex flex-col divide-y divide-white/10 border-y border-white/10">
              {FRACTIONAL.blocks.map((b, i) => (
                <Reveal key={b.label} delay={0.1 + i * 0.1}>
                  <div className="grid gap-4 py-8 sm:grid-cols-[150px_1fr] sm:gap-8" data-testid={`fractional-block-${i + 1}`}>
                    <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-gold/80">{b.label}</h3>
                    <div>
                      <p className="font-display text-lg sm:text-xl font-bold tracking-[-0.03em] text-white">{b.title}</p>
                      <p className="prose-justify mt-3 text-sm text-slate-400 leading-relaxed">{b.body}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <div className="mt-20 border-t border-white/10 pt-14 lg:mt-24" data-testid="fractional-fit-check">
            <Reveal className="flex flex-wrap items-end justify-between gap-4">
              <h3 className="font-display text-2xl sm:text-3xl font-bold tracking-[-0.03em] text-white">
                Is fractional right for you?
              </h3>
              <p className="max-w-sm text-sm text-slate-400 leading-relaxed">
                If one or more of these sounds familiar, it is worth a conversation.
              </p>
            </Reveal>
            <div className="mt-10 grid gap-px border border-white/10 bg-white/10 md:grid-cols-3">
              {FRACTIONAL.fit.map((f, i) => (
                <Reveal key={f.sign} delay={0.08 + i * 0.08} className="h-full">
                  <div className="flex h-full flex-col gap-4 bg-navy p-7 transition-colors duration-300 hover:bg-navy-light/60" data-testid={`fractional-fit-sign-${i + 1}`}>
                    <div className="flex items-center gap-3">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center border border-gold/60 text-gold" aria-hidden="true">
                        <Check size={12} strokeWidth={2.5} />
                      </span>
                      <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500">Sign 0{i + 1}</span>
                    </div>
                    <p className="font-display text-lg font-bold tracking-[-0.03em] leading-snug text-white">{f.sign}</p>
                    <p className="text-sm text-slate-400 leading-relaxed">{f.detail}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <div className="mt-20 border-t border-white/10 pt-16 lg:mt-24 lg:pt-20">
            <NinetyDayTimeline />
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 py-20 lg:py-28" data-testid="services-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-start gap-8 lg:flex-row lg:items-center lg:justify-between">
          <Reveal>
            <h2 className="font-display max-w-xl text-2xl sm:text-3xl font-bold tracking-[-0.03em] text-white">
              Not sure where to start? Start with the market.
            </h2>
            <p className="prose-justify mt-4 max-w-xl text-base text-slate-400 leading-relaxed">
              A short conversation is usually enough to identify where better intelligence would
              make the biggest difference to your pipeline.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="services-book-consultation-button"
              className="group inline-flex items-center gap-2 bg-gold px-7 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-navy transition-all duration-300 hover:bg-gold-glow"
            >
              Book a Consultation
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
